/**
 * Redux Slice pour le système de monitoring
 * Gestion de l'état du dashboard, filtres et préférences (les données viennent de RTK Query)
 * @author GitHub Copilot
 * @date 2025-07-11
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  MonitoringFilters,
  MonitoringPreferences,
  MonitoringAlert,
  MonitoringEvent
} from '../../types/monitoring.types';

// Simplified state for RTK Query integration
interface MonitoringSliceState {
  dashboardState: {
    isLoading: boolean;
    error: string | null;
    lastUpdated: Date | null;
    autoRefresh: boolean;
    refreshInterval: number; // en secondes
  };
  filters: MonitoringFilters;
  preferences: MonitoringPreferences;
  alerts: MonitoringAlert[];
  events: MonitoringEvent[];
}

// État initial simplifié (les métriques viennent de RTK Query)
const initialState: MonitoringSliceState = {
  dashboardState: {
    isLoading: false,
    error: null,
    lastUpdated: null,
    autoRefresh: true,
    refreshInterval: 30 // 30 secondes
  },
  filters: {
    dateRange: 'today',
    selectedAgents: [],
    showInactive: false
  },
  preferences: {
    theme: 'light',
    chartType: 'line',
    showAnimations: true,
    autoRefreshEnabled: true,
    refreshInterval: 30,
    defaultView: 'overview'
  },
  alerts: [],
  events: []
};

// Slice Redux simplifié
const monitoringSlice = createSlice({
  name: 'monitoring',
  initialState,
  reducers: {
    // Actions pour le dashboard
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.dashboardState.isLoading = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.dashboardState.error = action.payload;
    },
    
    clearError: (state) => {
      state.dashboardState.error = null;
    },
    
    setLastUpdated: (state, action: PayloadAction<Date>) => {
      state.dashboardState.lastUpdated = action.payload;
    },
    
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.dashboardState.autoRefresh = action.payload;
      state.preferences.autoRefreshEnabled = action.payload;
    },
    
    setRefreshInterval: (state, action: PayloadAction<number>) => {
      state.dashboardState.refreshInterval = action.payload;
      state.preferences.refreshInterval = action.payload;
    },

    // Actions pour les filtres
    updateFilters: (state, action: PayloadAction<Partial<MonitoringFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    resetFilters: (state) => {
      state.filters = {
        dateRange: 'today',
        selectedAgents: [],
        showInactive: false
      };
    },

    // Actions pour les préférences
    updatePreferences: (state, action: PayloadAction<Partial<MonitoringPreferences>>) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },

    // Actions pour les alertes
    addAlert: (state, action: PayloadAction<Omit<MonitoringAlert, 'id' | 'timestamp' | 'isRead'>>) => {
      const newAlert: MonitoringAlert = {
        id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        isRead: false,
        ...action.payload
      };
      state.alerts.unshift(newAlert);
      
      // Garder seulement les 50 dernières alertes
      if (state.alerts.length > 50) {
        state.alerts = state.alerts.slice(0, 50);
      }
    },
    
    markAlertAsRead: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find(a => a.id === action.payload);
      if (alert) {
        alert.isRead = true;
      }
    },
    
    removeAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
    },
    
    clearAllAlerts: (state) => {
      state.alerts = [];
    },

    // Actions pour les événements
    logEvent: (state, action: PayloadAction<Omit<MonitoringEvent, 'timestamp'>>) => {
      const newEvent: MonitoringEvent = {
        timestamp: new Date(),
        ...action.payload
      };
      state.events.unshift(newEvent);
      
      // Garder seulement les 100 derniers événements
      if (state.events.length > 100) {
        state.events = state.events.slice(0, 100);
      }
    },
    
    clearEvents: (state) => {
      state.events = [];
    }
  }
});

// Export des actions
export const {
  setLoading,
  setError,
  clearError,
  setLastUpdated,
  setAutoRefresh,
  setRefreshInterval,
  updateFilters,
  resetFilters,
  updatePreferences,
  addAlert,
  markAlertAsRead,
  removeAlert,
  clearAllAlerts,
  logEvent,
  clearEvents
} = monitoringSlice.actions;

// Sélecteurs
export const selectDashboardState = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.dashboardState;

export const selectFilters = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.filters;

export const selectPreferences = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.preferences;

export const selectAlerts = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.alerts;

export const selectUnreadAlerts = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.alerts.filter(alert => !alert.isRead);

export const selectEvents = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.events;

export const selectIsLoading = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.dashboardState.isLoading;

export const selectError = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.dashboardState.error;

export const selectAutoRefresh = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.dashboardState.autoRefresh;

export const selectRefreshInterval = (state: { monitoring: MonitoringSliceState }) => 
  state.monitoring.dashboardState.refreshInterval;

// Export du reducer
export default monitoringSlice.reducer;
